import React, { useCallback, useEffect, useState } from 'react'
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Paper from '@material-ui/core/Paper';
import Container from '@material-ui/core/Container';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'react-cookie';
import { ALL_FUNC_COOKIES, COOKIES_NAME, COOKIES_REMOVE_NAME, FUNCT_COOKIES } from '../constants';
import { gtmInitialize } from '../functions/ga';
import CookieContentComponent from './CookieContentComponent';
import CookieSwitcherComponent from './CookieSwitcherComponent';
import { COOKIES_ROUTE } from '../constants/routes';
import removeFunctCookies from '../functions/removeFunctCookies';
import LinkComponent from './LinkComponent';

const useStyles = makeStyles((theme) => ({
    cookie: {
        width: '100%',
        margin: 'auto',
        padding: theme.spacing(4),
        borderRadius: 10,
        maxHeight: '80vh',
        overflowY: 'scroll',
        [theme.breakpoints.down('xs')]: {
            padding: `${theme.spacing(4)}px ${theme.spacing(2)}px`,
        },
    },
    cont: {
        position: 'fixed',
        zIndex: 101,
        bottom: 0,
        top: 0,
        display: 'flex',
        margin: 'auto',
        left: 0,
        right: 0,
        visibility: 'hidden',
        transition: 'transform 0.4s ease-in-out, visibility 0.2s 0.4s ease-in',
        transform: 'translate(0, 150%)',
        maxHeight: '80vh',
    },
    content: {
        '& a': {
            textDecoration: 'none',
            color: theme.palette.primary.main,
        },
    },
    activeCookie: {
        visibility: 'visible',
        transition: 'transform 0.4s ease-in-out',
        transform: 'translate(0, 0)',
    },
    link: {
        marginTop: theme.spacing(4),
    }
}))

const CookiesComponent = ({ cookiesAccess }) => {
    const classes = useStyles();
    const [tabValue, setTabValue] = useState(0);
    const [funcActive, setFuncActive] = useState(true);
    const [marketingActive, setMarketingActive] = useState(true);
    const [statisticsActive, setStatisticsActive] = useState(true);
    const [cookies, setCookie, removeCookie] = useCookies([ALL_FUNC_COOKIES, COOKIES_NAME, COOKIES_REMOVE_NAME, ...FUNCT_COOKIES]);
    const [showCookies, setShowCookies] = useState(false);
    const [closeCookies, setCloseCookies] = useState(false);
    const { t } = useTranslation();

    const createCookie = useCallback((name) => {
        setCookie(name, true, {
            expires: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
        });
    }, [setCookie])
    const handleStatistic = useCallback(() => {
        createCookie(COOKIES_NAME)
        gtmInitialize();
        if (window.dataLayer) {
            window.dataLayer.push({
                event: 'Pageview',
            });
        }
    }, [createCookie])



    const handleCustom = useCallback(() => {
        setTabValue(1);
    }, [])

    const handleCustomAccept = useCallback(() => {
        if (statisticsActive) {
            handleStatistic();
        } else {
            removeCookie(COOKIES_NAME)
            createCookie(COOKIES_REMOVE_NAME)
        }

        if (funcActive) {
            removeCookie(ALL_FUNC_COOKIES);
        } else {
            createCookie(ALL_FUNC_COOKIES)
            removeFunctCookies(cookies)
        }
        setCloseCookies(true);
    }, [statisticsActive, handleStatistic, cookies, createCookie, removeCookie, funcActive])

    const handleNecessary = useCallback(() => {
        removeCookie(COOKIES_NAME)
        createCookie(COOKIES_REMOVE_NAME)
        createCookie(ALL_FUNC_COOKIES)
        removeFunctCookies(cookies)
        setCloseCookies(true);
    }, [removeCookie, createCookie, cookies])
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const showCookiesParam = urlParams.get('show_customize_cookies');
        if (showCookiesParam) {
            urlParams.delete('show_customize_cookies');
            window.history.replaceState(null, '', '?' + urlParams + window.location.hash)
            setShowCookies(1);
            setTabValue(1);
        }
        const scrollCheck = () => {
            if (
                !showCookies
                && window.pageYOffset >= 100
                && !cookies[COOKIES_NAME]
                && !cookies[COOKIES_REMOVE_NAME]
            ) {
                setShowCookies(true);
            }
        }
        window.addEventListener('scroll', scrollCheck);
        return (() => {
            window.removeEventListener('scroll', scrollCheck);
        })
        // eslint-disable-next-line 
    }, [cookies])


    const handleFullAccess = useCallback(() => {
        handleStatistic()
        removeCookie(ALL_FUNC_COOKIES);
        setCloseCookies(true);
    }, [removeCookie, handleStatistic]);


    const handleStatistics = useCallback(() => {
        setStatisticsActive(!statisticsActive)
    }, [statisticsActive])

    const handleFunc = useCallback(() => {
        setFuncActive(!funcActive)
    }, [funcActive])

    const handleMarketing = useCallback(() => {
        setMarketingActive(!marketingActive)
    }, [marketingActive])

    return (
        <Container className={`${classes.cont} ${!closeCookies && showCookies ? classes.activeCookie : ''}`} maxWidth='sm'>
            <Paper elevation={3} className={classes.cookie} >
                <CookieContentComponent
                    tabValue={tabValue}
                    value={0}
                    title={t('cookies.title')}
                    showCustomTitle
                    handleAccept={handleFullAccess}
                    handleCustom={handleCustom}
                    handleSecondBtn={handleNecessary}
                    secondBtn={t('buttons.customize')}
                >
                    <Typography className={classes.content} dangerouslySetInnerHTML={{ __html: t('cookies.content', { href: `${window.location.origin}/${COOKIES_ROUTE}` }) }} />
                </CookieContentComponent>
                <CookieContentComponent
                    tabValue={tabValue}
                    value={1}
                    title={t('cookies.customize_title')}
                    handleAccept={handleFullAccess}
                    handleSecondBtn={handleCustomAccept}
                    secondBtn={t('buttons.save_configuration')}
                >
                    <CookieSwitcherComponent
                        active
                        disabled
                        title={t('cookies.necessary')}
                        desc={t('cookies.necessary_desc')}
                    />
                    <CookieSwitcherComponent
                        active={statisticsActive}
                        title={t('cookies.statistics')}
                        desc={t('cookies.statistics_desc')}
                        handleChange={handleStatistics}
                    />
                    <CookieSwitcherComponent
                        title={t('cookies.marketing')}
                        desc={t('cookies.marketing_desc')}
                        active={marketingActive}
                        handleChange={handleMarketing}
                    />
                    <CookieSwitcherComponent
                        active={funcActive}
                        title={t('cookies.functionality')}
                        desc={t('cookies.functionality_desc')}
                        handleChange={handleFunc}
                        hiddenHr
                    />
                    <LinkComponent className={classes.link} title={t('cookies.link_title')} href={COOKIES_ROUTE} />
                </CookieContentComponent>
            </Paper>
        </Container>
    )
}


export default React.memo(CookiesComponent);